// src/store/thunks/branchThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import branchService from "../../services/branchService";

// Branch Management Thunks
export const createBranchThunk = createAsyncThunk(
  "branch/createBranch",
  async (branchData, { rejectWithValue }) => {
    try {
      const response = await branchService.createBranch(branchData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

//only active branches
export const getBranchesThunk = createAsyncThunk(
  "branch/getBranches",
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranches({
        ...queryParams,
        isActive: true,
        status: "active",
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllBranchesThunk = createAsyncThunk(
  "branch/getAllBranches",
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranches(queryParams);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchThunk = createAsyncThunk(
  "branch/getBranch",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranch(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchThunkByCode = createAsyncThunk(
  "branch/getBranchByCode",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranchByCode(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBranchThunk = createAsyncThunk(
  "branch/updateBranch",
  async ({ branchId, branchData }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateBranch(branchId, branchData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const configureRateModeThunk = createAsyncThunk(
  "branch/configureRateMode",
  async ({ branchId, rateConfig }, { rejectWithValue }) => {
    try {
      const response = await branchService.configureRateMode(
        branchId,
        rateConfig
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const configureHybridPolicyThunk = createAsyncThunk(
  "branch/configureHybridPolicy",
  async ({ branchId, hybridConfig }, { rejectWithValue }) => {
    try {
      const response = await branchService.configureHybridPolicy(
        branchId,
        hybridConfig
      );
      return { branchId, response };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const setMainBranchThunk = createAsyncThunk(
  "branch/setMainBranch",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.setMainBranch(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const copyConfigurationThunk = createAsyncThunk(
  "branch/copyConfiguration",
  async ({ sourceBranchId, targetBranchId }, { rejectWithValue }) => {
    try {
      const response = await branchService.copyConfiguration(
        sourceBranchId,
        targetBranchId
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deactivateBranchThunk = createAsyncThunk(
  "branch/deactivateBranch",
  async ({ branchId, reason }, { rejectWithValue }) => {
    try {
      const response = await branchService.deactivateBranch(branchId, reason);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchAnalyticsThunk = createAsyncThunk(
  "branch/getBranchAnalytics",
  async ({ branchId, dateRange }, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranchAnalytics(
        branchId,
        dateRange
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchHierarchyThunk = createAsyncThunk(
  "branch/getBranchHierarchy",
  async (_, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranchHierarchy();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Store Management Thunks
export const createStoreThunk = createAsyncThunk(
  "branch/createStore",
  async ({ branchId, storeData }, { rejectWithValue }) => {
    try {
      const response = await branchService.createStore(branchId, storeData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchStoresThunk = createAsyncThunk(
  "branch/getBranchStores",
  async ({ branchId, queryParams }, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranchStores(
        branchId,
        queryParams
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateStoreThunk = createAsyncThunk(
  "branch/updateStore",
  async ({ branchId, storeId, storeData }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateStore(
        branchId,
        storeId,
        storeData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteStoreThunk = createAsyncThunk(
  "branch/deleteStore",
  async ({ branchId, storeId }, { rejectWithValue }) => {
    try {
      const response = await branchService.deleteStore(branchId, storeId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const transferStoreThunk = createAsyncThunk(
  "branch/transferStore",
  async ({ branchId, storeId, newBranchId }, { rejectWithValue }) => {
    try {
      const response = await branchService.transferStore(
        branchId,
        storeId,
        newBranchId
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getStoreMetricsThunk = createAsyncThunk(
  "branch/getStoreMetrics",
  async ({ branchId, storeId, dateRange }, { rejectWithValue }) => {
    try {
      const response = await branchService.getStoreMetrics(
        branchId,
        storeId,
        dateRange
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk Operations Thunks
export const bulkUpdateBranchesThunk = createAsyncThunk(
  "branch/bulkUpdateBranches",
  async ({ branchIds, updateData }, { rejectWithValue }) => {
    try {
      const response = await branchService.bulkUpdateBranches(
        branchIds,
        updateData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkDeactivateBranchesThunk = createAsyncThunk(
  "branch/bulkDeactivateBranches",
  async ({ branchIds, reason }, { rejectWithValue }) => {
    try {
      const response = await branchService.bulkDeactivateBranches(
        branchIds,
        reason
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Add these thunks to your existing branchThunks.js

export const validateInheritanceThunk = createAsyncThunk(
  "branch/validateInheritance",
  async ({ branchId, targetBranchId }, { rejectWithValue }) => {
    try {
      const response = await branchService.validateInheritance(
        branchId,
        targetBranchId
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getInheritanceSourcesThunk = createAsyncThunk(
  "branch/getInheritanceSources",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.getInheritanceSources(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getInheritanceChainThunk = createAsyncThunk(
  "branch/getInheritanceChain",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.getInheritanceChain(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getDependentBranchesThunk = createAsyncThunk(
  "branch/getDependentBranches",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.getDependentBranches(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Add these thunks to your existing branchThunks.js file

// Branch Owner Management Thunks
export const updateBranchOwnerThunk = createAsyncThunk(
  "branch/updateBranchOwner",
  async ({ branchId, ownerId }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateBranchOwner(branchId, ownerId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminPrimaryBranchThunk = createAsyncThunk(
  "branch/updateAdminPrimaryBranch",
  async ({ adminId, branchCode }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateAdminPrimaryBranch(
        adminId,
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminSecondaryBranchesThunk = createAsyncThunk(
  "branch/updateAdminSecondaryBranches",
  async ({ adminId, branchCodes }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateAdminSecondaryBranches(
        adminId,
        branchCodes
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Exchange Charges Management Thunks
export const getExchangeChargesThunk = createAsyncThunk(
  "branch/getExchangeCharges",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.getExchangeCharges(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateExchangeChargesThunk = createAsyncThunk(
  "branch/updateExchangeCharges",
  async ({ branchId, exchangeCharges }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateExchangeCharges(
        branchId,
        exchangeCharges
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getDefaultExchangeChargesThunk = createAsyncThunk(
  "branch/getDefaultExchangeCharges",
  async (_, { rejectWithValue }) => {
    try {
      const response = await branchService.getDefaultExchangeCharges();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const compareExchangeChargesThunk = createAsyncThunk(
  "branch/compareExchangeCharges",
  async (branchCodes, { rejectWithValue }) => {
    try {
      const response = await branchService.compareExchangeCharges(branchCodes);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const copyExchangeChargesThunk = createAsyncThunk(
  "branch/copyExchangeCharges",
  async ({ sourceBranchId, targetBranchIds }, { rejectWithValue }) => {
    try {
      const response = await branchService.copyExchangeCharges(
        sourceBranchId,
        targetBranchIds
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const validateExchangeChargesThunk = createAsyncThunk(
  "branch/validateExchangeCharges",
  async (exchangeCharges, { rejectWithValue }) => {
    try {
      const response = await branchService.validateExchangeCharges(
        exchangeCharges
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetExchangeChargesThunk = createAsyncThunk(
  "branch/resetExchangeCharges",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await branchService.resetExchangeCharges(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Actions for managing exchange charges state
export const clearExchangeChargesError = createAction(
  "branch/clearExchangeChargesError"
);
export const setExchangeChargesMode = createAction(
  "branch/setExchangeChargesMode"
);

// Actions for managing inheritance state
export const clearInheritanceValidation = createAction(
  "branch/clearInheritanceValidation"
);
export const setInheritanceSource = createAction("branch/setInheritanceSource");

// Action to clear branch errors
export const clearBranchError = createAction("branch/clearBranchError");

// Action to reset selected branch
export const resetSelectedBranch = createAction("branch/resetSelectedBranch");

// Action to reset store data
export const resetStoreData = createAction("branch/resetStoreData");
